/**
 * Buscador principal, categorías y función para compartir el catálogo.
 */
if (!customElements.get('balper-header-search')) {
  customElements.define('balper-header-search', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      if (this.hasAttribute('data-use-collection-order')) {
        const currentUrl = new URL(location.href);
        if (currentUrl.searchParams.has('sort_by')) {
          currentUrl.searchParams.delete('sort_by');
          location.replace(currentUrl.href);
          return;
        }
      }
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="q"]');
      this.categoryLinks = Array.from(this.querySelectorAll('[data-balper-category-link]'));
      this.categoryGroups = Array.from(this.querySelectorAll('[data-balper-category-group]'));
      this.categoryGroups.forEach((group) => {
        group.addEventListener('toggle', () => {
          if (!group.open) return;
          this.categoryGroups.forEach((otherGroup) => {
            if (otherGroup !== group) otherGroup.removeAttribute('open');
          });
        }, options);
      });
      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.categoryGroups.forEach((group) => group.removeAttribute('open'));
      }, options);
      this.syncCategories = () => {
        if (this.hasAttribute('data-collection-navigation')) {
          const activeCategory = this.categoryLinks.find((link) => link.classList.contains('is-active'));
          if (activeCategory) {
            requestAnimationFrame(() => activeCategory.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' }));
          }
          return;
        }

        const parameters = new URL(location.href).searchParams;
        const categoryParam = this.categoryLinks[0]?.dataset.categoryParam;
        const activeCategories = new Set(categoryParam ? parameters.getAll(categoryParam) : []);

        this.categoryLinks.forEach((link) => {
          const value = link.dataset.categoryValue || '';
          const isActive = value ? activeCategories.has(value) : activeCategories.size === 0;
          link.classList.toggle('is-active', isActive);
          if (isActive) {
            link.setAttribute('aria-current', 'page');
          } else {
            link.removeAttribute('aria-current');
          }
        });

        const activeCategory = this.categoryLinks.find((link) => link.classList.contains('is-active'));
        if (activeCategory?.dataset.categoryValue) {
          requestAnimationFrame(() => activeCategory.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' }));
        }
      };
      this.categoryLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.button !== 0 || event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) return;
          const categoryParam = link.dataset.categoryParam;
          if (!categoryParam) return;

          const destination = new URL(location.href);
          destination.searchParams.delete(categoryParam);
          destination.searchParams.delete('buscar');
          destination.searchParams.delete('page');
          if (link.dataset.categoryValue) {
            destination.searchParams.append(categoryParam, link.dataset.categoryValue);
          }

          event.preventDefault();
          location.assign(destination.href);
        }, options);
      });
      this.addEventListener('submit', (event) => {
        if (event.defaultPrevented) return;
        const catalogForm = document.querySelector('form.buscador-catalogo');
        if (!catalogForm || catalogForm.dataset.balperIniciado !== 'true') return;
        const catalogInput = catalogForm.querySelector('input[name="q"]');
        if (!catalogInput) return;
        event.preventDefault();
        catalogInput.value = this.input.value.trim();
        catalogForm.requestSubmit();
      }, options);
      this.addEventListener('reset', () => {
        const catalogForm = document.querySelector('form.buscador-catalogo');
        if (!catalogForm || !new URLSearchParams(location.search).get('buscar')) return;
        const catalogInput = catalogForm.querySelector('input[name="q"]');
        if (catalogInput && catalogForm.dataset.balperIniciado === 'true') {
          catalogInput.value = '';
          catalogInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        let handled = false;
        const predictiveSearch = this.querySelector('predictive-search');
        if (predictiveSearch?.isOpen) {
          predictiveSearch.close();
          handled = true;
        }
        this.categoryGroups.forEach((group) => {
          if (group.open) {
            group.removeAttribute('open');
            handled = true;
          }
        });
        if (handled) event.preventDefault();
      }, options);
      this.initialize = () => {
        document.documentElement.classList.add('balper-header-search-ready');
        this.syncCategories();
        if (document.querySelector('form.buscador-catalogo')) {
          this.input.value = new URLSearchParams(location.search).get('buscar') || '';
          this.querySelector('[type="reset"]').classList.toggle('hidden', !this.input.value);
        }
        this.setupCatalogTools();
      };
      this.setupCatalogTools = () => {
        const toolbar = document.querySelector('[data-balper-catalog-toolbar]');
        const shareButton = toolbar?.querySelector('[data-balper-toolbar-share]');
        if (!toolbar || !shareButton || toolbar.dataset.balperToolsReady === 'true') return;
        toolbar.dataset.balperToolsReady = 'true';

        const defaultShareContent = shareButton.innerHTML;
        shareButton.addEventListener('click', async () => {
          const publicUrl = new URL(location.href);
          ['preview_theme_id', '_fd', 'pb'].forEach((parameter) => publicUrl.searchParams.delete(parameter));
          const hasSelection = publicUrl.searchParams.has('buscar') || publicUrl.searchParams.has('q') || Array.from(publicUrl.searchParams.keys()).some((parameter) => parameter.startsWith('filter.'));
          const shareData = {
            title: document.title,
            text: hasSelection ? 'Mira esta selección de productos de Balper Export' : 'Revisa el catálogo de Balper Export',
            url: publicUrl.href
          };

          try {
            if (navigator.share) {
              await navigator.share(shareData);
            } else if (navigator.clipboard) {
              await navigator.clipboard.writeText(publicUrl.href);
              shareButton.textContent = 'Enlace copiado';
              window.setTimeout(() => { shareButton.innerHTML = defaultShareContent; }, 2200);
            } else {
              window.prompt('Copia este enlace:', publicUrl.href);
            }
          } catch (error) {
            if (error.name !== 'AbortError') window.prompt('Copia este enlace:', publicUrl.href);
          }
        }, options);

      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.initialize, { ...options, once: true });
      } else {
        this.initialize();
      }
      const productGrid = document.querySelector('#ProductGridContainer');
      if (productGrid && this.categoryLinks.length) {
        this.categoryObserver = new MutationObserver(this.syncCategories);
        this.categoryObserver.observe(productGrid, { childList: true, subtree: true });
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.categoryObserver?.disconnect();
      document.documentElement.classList.remove('balper-header-search-ready');
    }
  });
}
